import { SetterOrUpdater } from "recoil";
import {
  BabyPeekReveal,
  RequisitionModelSerializer,
  UserAuthClientResponse,
  UserProfileAuthClientResponse,
} from "../../recoil/types";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { TypographySize } from "../shared/Typography/Typography.styles";
import {
  BABYPEEK_URL,
  BABYPEEK_URL_FAQ,
  BILLING_MULTIPLE_URL,
  BILLING_URL,
  HELP_PAGE_URL,
  LANDING_PAGE_URL,
  LOGIN_PAGE_URL,
  PROFILE_URL,
} from "../../utils/urlConstants";
import { ColorPalette } from "../../theme";
import {
  HeaderAccordionGrid,
  HeaderAccordionWrapper,
  HeaderIconContainerBabyPeekUnlockContainer,
} from "./Header.styles";
import { DeviceType } from "../../utils/hooks/useDeviceHook";
import { removeCookieAccessToken } from "../../utils/requestUtils";
import { BabyPeekAccordionHeader } from "./BabyPeekAccordionHeader";
import i18n from "../../i18n/config";
import { shouldShowBabyPeekNav } from "../BabyPeek/Services/BabyPeekService";

interface Header {
  header: React.ReactElement;
  key: string;
  hide?: boolean;
}

export const GetHeaderMenuLinks = (
  typographyColor: keyof ColorPalette,
  fontSize: TypographySize,
  activeHeader: string,
  setActiveHeader: (header: string) => void,
  patientProfileInfo: UserProfileAuthClientResponse | undefined,
  setPatientInfo: SetterOrUpdater<UserAuthClientResponse | undefined>,
  setProfileInfo: SetterOrUpdater<UserProfileAuthClientResponse | undefined>,
  deviceType: DeviceType,
  billingReqs?: RequisitionModelSerializer[],
  babyPeek?: {
    status: BabyPeekReveal | undefined;
    babyPeekPK: string | undefined;
  },
  setNavBarToggle?: () => void,
): Header[] => {
  const headerItems: Header[] = [];
  headerItems.push({
    header: GetTypographyContent({
      content: i18n.t("navigation.home"),
      size: fontSize,
      padding: "20px",
      link: LANDING_PAGE_URL,
      textColor: `${
        activeHeader.includes(LANDING_PAGE_URL) ? "tealAccent" : typographyColor
      }`,
      onClick: () => {
        setActiveHeader(LANDING_PAGE_URL);
        if (setNavBarToggle) setNavBarToggle();
      },
    }),
    key: "home",
  });
  headerItems.push({
    header: GetTypographyContent({
      content: i18n.t("navigation.help"),
      size: fontSize,
      padding: "20px",
      link: HELP_PAGE_URL,
      textColor: `${
        activeHeader.includes(HELP_PAGE_URL) ? "tealAccent" : typographyColor
      }`,
      onClick: () => {
        setActiveHeader(HELP_PAGE_URL);
        if (setNavBarToggle) setNavBarToggle();
      },
    }),
    key: "help",
  });
  headerItems.push({
    header: GetTypographyContent({
      content: i18n.t("navigation.billing"),
      size: fontSize,
      padding: "20px",
      link:
        billingReqs && billingReqs.length > 1
          ? BILLING_MULTIPLE_URL
          : `${BILLING_URL}/${
              billingReqs?.length && billingReqs[0]
                ? billingReqs[0].bill_pk
                : undefined
            }`,
      textColor: `${
        activeHeader.includes(BILLING_URL) ? "tealAccent" : typographyColor
      }`,
      onClick: () => {
        setActiveHeader(BILLING_URL);
        if (setNavBarToggle) setNavBarToggle();
      },
    }),

    key: "internalBill",
    hide: !billingReqs?.length,
  });

  const getBabyPeekMenu = () => {
    if (!babyPeek?.status || !shouldShowBabyPeekNav(babyPeek.status)) {
      return undefined;
    }
    const isBabyPeekActive =
      activeHeader.includes(BABYPEEK_URL) ||
      activeHeader.includes(BABYPEEK_URL_FAQ);
    const babyPeekSubItemColor: keyof ColorPalette = typographyColor;
    return {
      header:
        babyPeek?.status === "pending" ? (
          GetTypographyContent({
            content: (
              <>
                BabyPeek
                <HeaderIconContainerBabyPeekUnlockContainer>
                  {i18n.t("navigation.babyPeek.unlock")}
                </HeaderIconContainerBabyPeekUnlockContainer>
              </>
            ),
            size: fontSize,
            padding: "20px",
            link: `${BABYPEEK_URL}/${babyPeek?.babyPeekPK}/${"intro"}?loc=menu`,
            textColor: `${
              activeHeader.includes(BABYPEEK_URL)
                ? "tealAccent"
                : typographyColor
            }`,
            onClick: () => {
              setActiveHeader(BABYPEEK_URL);
              if (setNavBarToggle) setNavBarToggle();
            },
          })
        ) : (
          <HeaderAccordionWrapper
            deviceType={deviceType}
            isActive={isBabyPeekActive}
          >
            <BabyPeekAccordionHeader
              closeOnMouseLeave={deviceType === "desktop"}
              data={[
                {
                  title: GetTypographyContent({
                    content: "BabyPeek",
                    size: fontSize,
                    padding: "20px",
                    textColor: isBabyPeekActive
                      ? "tealAccent"
                      : typographyColor,
                  }),
                  content: (
                    <HeaderAccordionGrid>
                      <div data-testid="baby-peek-results-link">
                        {GetTypographyContent({
                          content: i18n.t("navigation.babyPeek.results"),
                          size: fontSize,
                          padding: "20px",
                          link: `${BABYPEEK_URL}/${
                            babyPeek?.babyPeekPK
                          }/${"summary"}?loc=menu`,
                          textColor: babyPeekSubItemColor,
                          onClick: () => {
                            setActiveHeader(BABYPEEK_URL);
                            if (setNavBarToggle) setNavBarToggle();
                          },
                        })}
                      </div>
                      <div>
                        {GetTypographyContent({
                          content: i18n.t("navigation.babyPeek.games"),
                          size: fontSize,
                          padding: "20px",
                          link: `${BABYPEEK_URL}/${
                            babyPeek?.babyPeekPK
                          }/${"games"}?loc=menu`,
                          textColor: babyPeekSubItemColor,
                          onClick: () => {
                            setActiveHeader(BABYPEEK_URL);
                            if (setNavBarToggle) setNavBarToggle();
                          },
                        })}
                      </div>
                      <div>
                        {GetTypographyContent({
                          content: i18n.t("navigation.babyPeek.faqs"),
                          size: fontSize,
                          padding: "20px",
                          link: `${BABYPEEK_URL_FAQ}`,
                          textColor: babyPeekSubItemColor,
                          onClick: () => {
                            setActiveHeader(BABYPEEK_URL_FAQ);
                            if (setNavBarToggle) setNavBarToggle();
                          },
                        })}
                      </div>
                    </HeaderAccordionGrid>
                  ),
                  key: "babyPeekAccordion",
                },
              ]}
            />
          </HeaderAccordionWrapper>
        ),

      key: "babyPeek",
      hide: !babyPeek,
    };
  };
  const babyPeekMenu = getBabyPeekMenu();
  if (babyPeekMenu) {
    headerItems.push(babyPeekMenu);
  }

  headerItems.push({
    header: GetTypographyContent({
      content: i18n.t("navigation.profile"),
      size: fontSize,
      padding: "20px",
      link: PROFILE_URL,
      textColor: `${
        activeHeader.includes(PROFILE_URL) ? "tealAccent" : typographyColor
      }`,
      onClick: () => {
        setActiveHeader(PROFILE_URL);
        if (setNavBarToggle) setNavBarToggle();
      },
    }),
    key: "profile",
  });

  headerItems.push({
    header: GetTypographyContent({
      content: i18n.t("navigation.logout"),
      size: fontSize,
      padding: "20px",
      link: LOGIN_PAGE_URL,
      textColor: `${
        activeHeader.includes(LOGIN_PAGE_URL) ? "tealAccent" : typographyColor
      }`,
      onClick: () => {
        removeCookieAccessToken();
        setPatientInfo(undefined);
        setProfileInfo(undefined);
        sessionStorage.removeItem("current-pathname");
        if (setNavBarToggle) setNavBarToggle();
      },
    }),
    key: "logout",
  });

  return headerItems;
};

export const GetLoginHeader = (
  typographyColor: keyof ColorPalette,
  fontSize: TypographySize,
  setPatientInfo: SetterOrUpdater<UserAuthClientResponse | undefined>,
  loginActive: boolean,
  setNavBarToggle?: () => void,
): Header[] =>
  !loginActive
    ? [
        {
          header: GetTypographyContent({
            content: i18n.t("navigation.login"),
            size: fontSize,
            padding: "20px",
            link: LOGIN_PAGE_URL,
            textColor: typographyColor,
            onClick: () => {
              removeCookieAccessToken();
              setPatientInfo(undefined);
              if (setNavBarToggle) setNavBarToggle();
            },
          }),
          key: "login",
        },
      ]
    : [];
